import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { usePortal } from '../../context/PortalContext';
import PortalPageShell, { PortalEmpty, PortalLoading } from './PortalPageShell';
import ChildSelector from './ChildSelector';
import PortalScheduleSummary from './PortalScheduleSummary';
import { Card, CardContent } from '../ui/card';
import { formatTimeRange12h } from '../../utils/timeFormat';

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export default function PortalTimetable() {
  const { activeStudentId, portalMode } = usePortal();
  const [entries, setEntries] = useState([]);
  const [schoolHours, setSchoolHours] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const studentParams = portalMode === 'parent' && activeStudentId ? { student_id: activeStudentId } : {};
        const [timetableRes, hoursRes] = await Promise.all([
          api.get('/portal/student/timetable', { params: studentParams }),
          api.get(
            portalMode === 'parent' ? '/portal/parent/school-hours' : '/portal/student/school-hours',
            { params: studentParams },
          ),
        ]);
        if (!cancelled) {
          setEntries(timetableRes.data?.data ?? []);
          setSchoolHours(hoursRes.data?.data ?? null);
        }
      } catch {
        if (!cancelled) {
          setEntries([]);
          setSchoolHours(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeStudentId, portalMode]);

  const byDay = DAY_ORDER.reduce((acc, day) => {
    acc[day] = entries.filter((e) => (e.day_of_week || '').toLowerCase() === day);
    return acc;
  }, {});

  return (
    <PortalPageShell
      title="Class Timetable"
      description="Daily schedule"
      actions={portalMode === 'parent' ? <ChildSelector /> : null}
    >
      {!loading ? <PortalScheduleSummary schoolHours={schoolHours} /> : null}
      {loading ? <PortalLoading /> : null}
      {!loading && entries.length === 0 ? <PortalEmpty message="No timetable entries." /> : null}
      <div className="grid gap-4 md:grid-cols-2">
        {DAY_ORDER.map((day) => {
          const list = byDay[day];
          if (!list?.length) return null;
          return (
            <Card key={day}>
              <CardContent className="pt-4">
                <h3 className="font-semibold capitalize mb-2">{day}</h3>
                <ul className="space-y-2 text-sm">
                  {list.map((e) => (
                    <li key={e.id} className="flex justify-between gap-2 border-b border-border pb-2 last:border-0">
                      <span>
                        <span className="font-medium">{e.subject_name}</span>
                        <br />
                        <span className="text-muted-foreground text-xs">
                          {e.teacher_first_name} {e.teacher_last_name}
                        </span>
                      </span>
                      <span className="text-muted-foreground whitespace-nowrap">
                        {formatTimeRange12h(e.start_time, e.end_time)}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </PortalPageShell>
  );
}
